import { useCallback, useMemo, type MutableRefObject } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import type { ProductQuery } from "../../../data/productStore.types";
import type { ProductStore } from "../../../data/productStore";
import type { ProductStoreStatus } from "../../../hooks/useProductStore";
import {
  productStoreCacheId,
  type CatalogScope,
} from "../../../api/catalogScope";

const PAGE_SIZE = 100;

interface UseProductTableQueryParams {
  scope: CatalogScope | null;
  storeRef: MutableRefObject<ProductStore | null>;
  status: ProductStoreStatus;
  version: number;
  baseQuery: Omit<ProductQuery, "offset" | "limit">;
}

export const useProductTableQuery = ({
  scope,
  storeRef,
  status,
  version,
  baseQuery,
}: UseProductTableQueryParams) => {
  const cacheId = scope ? productStoreCacheId(scope) : null;
  const productsQuery = useInfiniteQuery({
    queryKey: ["productStore", cacheId, baseQuery, version],
    queryFn: ({ pageParam }) => {
      const store = storeRef.current;
      if (!store) {
        return Promise.resolve({ rows: [] as RestProduct[], total: 0 });
      }
      return store.query({ ...baseQuery, offset: pageParam, limit: PAGE_SIZE });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.rows.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    enabled: (status === "ready" || status === "syncing") && !!cacheId,
  });

  const rows = useMemo(
    () => productsQuery.data?.pages.flatMap((page) => page.rows) ?? [],
    [productsQuery.data],
  );
  const total = productsQuery.data?.pages[0]?.total ?? 0;
  const isLoading = productsQuery.isLoading;

  const loadMore = useCallback(() => {
    if (productsQuery.hasNextPage && !productsQuery.isFetchingNextPage) {
      void productsQuery.fetchNextPage();
    }
  }, [productsQuery]);

  return {
    rows,
    total,
    isLoading,
    loadMore,
  };
};
